import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { BusinessRegistration } from '../../../components/BusinessRegistration/index.tsx';
import { getBusinessPlans, createPaymentIntent } from '../../../services/api.ts';
import { BusinessPlan } from '../../../types/business.ts';
import { useNavigate } from 'react-router-dom';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);

const SectionContainer = styled.section`
  padding: 5rem 2rem;
  background: #f7fafc;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  color: #2d3748;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: #4a5568;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const PlanCard = styled.div<{ featured?: boolean }>`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: ${props => props.featured 
    ? '0 8px 16px rgba(66, 153, 225, 0.2)'
    : '0 4px 6px rgba(0, 0, 0, 0.1)'};
  border: ${props => props.featured ? '2px solid #4299e1' : 'none'};
  transform: ${props => props.featured ? 'scale(1.05)' : 'none'};
  transition: transform 0.2s;

  &:hover {
    transform: ${props => props.featured ? 'scale(1.07)' : 'translateY(-5px)'};
  }
`;

const PlanName = styled.h3`
  font-size: 1.5rem;
  color: #2d3748;
  margin-bottom: 1rem;
`;

const PlanPrice = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: #2d3748;
  margin-bottom: 1rem;

  span {
    font-size: 1rem;
    color: #718096;
  }
`;

const PlanDescription = styled.p`
  color: #4a5568;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
`;

const Feature = styled.li`
  color: #4a5568;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: #48bb78;
  }
`;

const PlanButton = styled.button<{ featured?: boolean }>`
  width: 100%;
  padding: 1rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.featured ? '#4299e1' : 'white'};
  color: ${props => props.featured ? 'white' : '#4299e1'};
  border: ${props => props.featured ? 'none' : '2px solid #4299e1'};

  &:hover {
    transform: translateY(-2px);
    background: ${props => props.featured ? '#3182ce' : '#ebf8ff'};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

export const PricingSection: React.FC = () => {
  const [plans, setPlans] = useState<BusinessPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<BusinessPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const data = await getBusinessPlans();
        setPlans(data);
      } catch (error) {
        console.error('Error loading plans:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, []);

  const handlePurchase = async (plan: BusinessPlan) => {
    setSelectedPlan(plan);
    setPaymentLoading(true);
    
    try {
      // Create a payment intent to get a client secret
      const { clientSecret: secret } = await createPaymentIntent({
        planId: plan.id,
        currency: 'eur'
      });
      
      setClientSecret(secret);
    } catch (error) {
      console.error('Error creating payment intent:', error);
      alert('Failed to initialize payment. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleRegistrationSuccess = (user: any) => {
    // Handle successful registration
    setSelectedPlan(null);
    setClientSecret(null);
    navigate('/registration-success');
  };

  const handleCloseModal = () => {
    setSelectedPlan(null);
    setClientSecret(null);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <SectionContainer id="pricing-section">
      <Content>
        <Header>
          <Title>Choose Your Plan</Title>
          <Subtitle>
            Select the perfect plan for your business needs. All plans include our
            core features with different levels of customization and support.
          </Subtitle>
        </Header>
        <PlansGrid>
          {plans.map((plan, index) => (
            <PlanCard key={index} featured={plan.featured}>
              <PlanName>{plan.name}</PlanName>
              <PlanPrice>
                €{plan.price}
                {plan.period && <span>{plan.period}</span>}
              </PlanPrice>
              <PlanDescription>{plan.description}</PlanDescription>
              <FeaturesList>
                {plan.features.map((feature, i) => (
                  <Feature key={i}>
                    <FontAwesomeIcon icon={faCheck} />
                    {feature}
                  </Feature>
                ))}
              </FeaturesList>
              <PlanButton 
                featured={plan.featured}
                onClick={() => handlePurchase(plan)}
              >
                Get Started
              </PlanButton>
            </PlanCard>
          ))}
        </PlansGrid>
      </Content>

      {selectedPlan && (
        <Modal onClick={handleCloseModal}>
          <ModalContent onClick={e => e.stopPropagation()}>
            {paymentLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                <p style={{ marginTop: '1rem' }}>Preparing payment form...</p>
              </div>
            ) : clientSecret ? (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <BusinessRegistration
                  planId={selectedPlan.id}
                  onSuccess={handleRegistrationSuccess}
                />
              </Elements>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p>Failed to initialize payment. Please try again.</p>
                <button 
                  onClick={handleCloseModal}
                  style={{ 
                    marginTop: '1rem', 
                    padding: '0.5rem 1rem', 
                    background: '#4299e1', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.25rem',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </ModalContent>
        </Modal>
      )}
    </SectionContainer>
  );
}; 